import React, { FC, useCallback, useEffect, useState } from "react";
import Page from "../common/navigation/Page";
import ListBlock from "../common/F7/ListBlock";
import ListBlockFormItem from "../common/F7/ListBlockFormItem";
import Button from "../common/F7/Button";
import BackButton from "../common/navigation/BackButton";
import FieldHandler from "../common/form/FieldHandler";
import FormSelector from "./components/FormSelector";
import _ from "lodash";
import { useNavigate } from "react-router-dom";
import { useFilterItem, useSetFilterItem } from "../../state";
import { useCurrentInventory } from "../../hooks/database/useCurrentInventory";
import { ProjectField, ProjectForm } from "../../hooks/database/types/projects";
import Spinner from "../common/F7/Spinner";
import * as sid from "../../utils/sid";
import { useForm } from "../../hooks/useForm";

const isNullOrWhitespace = (input: any) => {
  if (typeof input === "undefined" || input === null) {
    return true;
  }
  return !/\S/.test(input);
};

const AttributeFilterPage: FC = () => {
  const [currentForm, setCurrentForm] = useState<ProjectForm | null>(null);

  const cloneFields = useCallback((fields: Record<string, ProjectField>) => {
    return _.mapValues(
      fields,
      (field): ProjectField => ({
        ...field,
        // Clearing this is the reason for this function, in filtering no field is required
        required: false,
      })
    );
  }, []);

  const { fields, setValues, values, handleChange } = useForm(currentForm ? cloneFields(currentForm.fields) : {});

  const filterItem = useFilterItem();

  const { data: inventory } = useCurrentInventory();

  const handleFormChange = useCallback(
    (value: string) => {
      if (!inventory) {
        return;
      }
      if (value !== "") {
        setCurrentForm(inventory.forms[value]);
      } else {
        setCurrentForm(null);
      }
    },
    [inventory]
  );

  useEffect(() => {
    if (filterItem) {
      if (!currentForm || currentForm.key !== filterItem.form.key) {
        handleFormChange(filterItem.form.key);
        setValues(filterItem.values);
      }
    } else if (inventory) {
      handleFormChange(Object.keys(inventory.forms)[0]);
    }
  }, [handleFormChange, filterItem, inventory, setValues, currentForm]);

  const navigate = useNavigate();
  const setFilterItem = useSetFilterItem();
  const handleAddFilterButtonClick = useCallback(() => {
    const values_ = _.pickBy(values, _.negate(isNullOrWhitespace));
    setFilterItem({
      valueKeys: Object.keys(values_),
      key: "#Id:" + sid.generate(5),
      values: _.cloneDeep(values_),
      form: _.cloneDeep(currentForm!),
    });
    navigate("../list");
  }, [currentForm, values, navigate, setFilterItem]);

  const addEnabled = Object.keys(values).length > 0;

  if (!inventory) {
    return <Spinner />;
  }

  return (
    <Page noToolbar={true} navbarLeft={<BackButton>Close</BackButton>} title="Multiple Attribute Filter">
      <FormSelector forms={inventory.forms} selectedForm={currentForm?.key || ""} onChange={handleFormChange} />
      {Object.keys(fields).length > 0 ? (
        <ListBlock title="Fields to filter on">
          {Object.entries(fields)
            .filter(([_, field]) => field.type !== "image")
            .map(([key, field]) => (
              <ListBlockFormItem key={key} label={field.label!}>
                <FieldHandler field={field} onChange={handleChange} />
              </ListBlockFormItem>
            ))}
        </ListBlock>
      ) : null}
      <Button onClick={handleAddFilterButtonClick} disabled={!addEnabled}>
        Apply Filter
      </Button>
    </Page>
  );
};

export default AttributeFilterPage;
